import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Metafields, Product, ProductTypes, ProductVariant } from '@/types/ecommerce.types';
import { RootState } from '@/store';
import { FrameSizes, getMetafieldV2, mapProductEdges } from '@/utils/utils';
import SizeIcon from '../../templates/icons/SizeIcon';
import FrameIcon from '../../templates/icons/FrameIcon';
import GeneralIcon from '@/components/templates/icons/GeneralIcon';
import ShapeIcon from '@/components/templates/icons/ShapeIcon';
import MaterialIcon from '@/components/templates/icons/MaterialIcon';
import { Container, ProductContainer, Wrapper } from './FrameBuilder.styles';
import { CenteredUploadButton, ModelViewerWrapper, SmallIcon } from './FrameBuilderPod.styles';
import { SectionTypes } from '../types';
import BottomButtons, { Buttons } from '@/components/product/frameBuilder/BottomButtons';
import ProductInfoV2 from '../header/ProductInfoV2';
import ColorIcon from '@/components/templates/icons/ColorIcon';
import ProductHeaderV2 from '../header/ProductHeaderV2';
import EditIconV2 from '@/components/templates/icons/EditIconV2';
import { Collection } from 'shopify-storefront-api-typings';
import { Color } from '../options/ProductSwatches';
import useScript from '@/hooks/useScript';
import useHideShopifyHeader from '@/hooks/useHideShopifyHeader';
import { usePostHog } from 'posthog-js/react';
import getProductDescription from '@/utils/getProductDescription';
import PreviewService from '@/services/PreviewService';
import UploadIcon from '@/components/templates/icons/UploadIcon';
import { calculatePrices } from '@/utils/formatPrice';
import type { PodFrameBuilderElement } from '@/types/pod-frame-builder';
import { getPodScriptConfig, PodDevBar } from './PodScriptToolbar';

type PropsType = {
  product: Product;
  shopMetafields?: Metafields;
  frameSizes: FrameSizes;
  variant?: ProductVariant | null;
  textARButton?: string;
  setActionTab?: (tab: SectionTypes | null) => void;
  className?: string;
  showProductInfo?: boolean;
  colorCollection?: Collection;
  showStyleButton?: boolean;
  frameBuilderHeight?: number;
};

const FrameBuilder = ({
  className,
  product,
  shopMetafields,
  frameSizes,
  variant,
  setActionTab,
  colorCollection,
  showProductInfo = true,
  textARButton = '',
  showStyleButton = false,
  frameBuilderHeight,
}: PropsType) => {
  const posthog = usePostHog();
  const { url, version, onError: podOnError } = getPodScriptConfig();
  const onScriptError = (event: ErrorEvent) => {
    posthog.capture('pod_script_load_error', {
      pod_version: version,
      message: event.message,
      filename: event.filename,
    });
    podOnError();
  };
  useScript(url, 'module', onScriptError);

  const modelViewer = useRef<PodFrameBuilderElement>(null);
  const colors: Color[] = mapProductEdges(colorCollection as Collection);
  const { items, type } = useSelector((state: RootState) => state.product);
  const [userImg, setUserImg] = useState(false);
  const [isGalleryZoomed, setIsGalleryZoomed] = useState(false);
  useHideShopifyHeader(isGalleryZoomed);

  const isGallery = type === ProductTypes.GALLERY;
  const isMobile = useMediaQuery({ maxWidth: 799 });

  const { color: productColor, printSize } = getProductDescription(product, shopMetafields);

  const galleryBackgroundImage =
    getMetafieldV2('gallery_background_image', product?.metafields) ?? '';
  const modelPosition = getMetafieldV2('3d_model_position', variant?.metafields) ?? '';

  const matGroupTitle = getMetafieldV2('mat_group_title', product.metafields);

  const isPuzzle = type === ProductTypes.PUZZLE;
  const isOrnament =
    type === ProductTypes.ORNAMENT ||
    (type === ProductTypes.GALLERY && product?.title?.toLowerCase().includes('ornament'));
  const noProductVariants = product?.variants?.edges?.length === 1;

  useEffect(() => {
    if (!modelViewer.current) return;

    const controller = new AbortController();

    modelViewer.current.addEventListener(
      'previews-change',
      (eve) => {
        console.log('previews-change', eve.detail);
        setUserImg(eve.detail?.previews?.length > 0);
      },
      { signal: controller.signal }
    );

    modelViewer.current.addEventListener(
      'images-change',
      (eve) => {
        console.log('images-change', eve.detail);
      },
      { signal: controller.signal }
    );

    modelViewer.current.addEventListener(
      'zoom-change',
      (eve) => setIsGalleryZoomed(eve.detail?.position !== null),
      { signal: controller.signal }
    );

    modelViewer.current.addEventListener(
      'ar-banner-click',
      () => {
        const form = document.querySelector<HTMLButtonElement>('#fn-atc-button');
        form?.click();
      },
      { signal: controller.signal }
    );

    modelViewer.current.addEventListener(
      'log',
      (eve) => {
        const log = eve.detail;
        if (log.severity !== 'error') return;
        posthog.capture(`pod_${log.code?.toLowerCase() ?? 'unknown_error'}`, {
          pod_version: version,
          severity: log.severity,
          message: log.message,
          code: log.code,
          detail: log.detail,
          sku: variant?.sku,
        });
      },
      { signal: controller.signal }
    );

    modelViewer.current.addEventListener(
      'load',
      async (e) => {
        const preview = await PreviewService.loadPreviewImage();
        if (preview && !preview.pod_id) {
          const newImage = await modelViewer.current?.setImage(preview.url);
          PreviewService.savePreviewImage({ ...preview, pod_id: newImage?.id });
        }
      },
      { signal: controller.signal }
    );

    return () => controller.abort();
  }, []);

  const bottomButtons = useMemo(() => {
    const leftButtons: Buttons[] = [];
    const rightButtons: Buttons[] = [];
    const buttons: Buttons[] = [];
    if (!noProductVariants) {
      buttons.push({
        onClick: () => setActionTab?.(isOrnament ? SectionTypes.SHAPE : SectionTypes.SIZE),
        imageComponent: isOrnament ? <ShapeIcon /> : <SizeIcon />,
        content: `${isOrnament ? SectionTypes.SHAPE : SectionTypes.SIZE}`,
        hideDesktop: true,
      });
    }

    if (isMobile && showStyleButton) {
      buttons.push({
        onClick: () => setActionTab?.(SectionTypes.STYLE),
        imageComponent: <FrameIcon />,
        content: SectionTypes.STYLE,
      });
    }

    if (!isPuzzle && colors.length > 1) {
      buttons.push({
        onClick: () => setActionTab?.(SectionTypes.COLOR),
        imageComponent: <ColorIcon />,
        content: SectionTypes.COLOR,
        hideDesktop: true,
      });
    }

    if (product?.mat_variants || product?.mat_product_group) {
      buttons.push({
        onClick: () => setActionTab?.(SectionTypes.MAT),
        imageComponent: matGroupTitle?.toLowerCase().includes('material') ? (
          <MaterialIcon />
        ) : matGroupTitle ? (
          <GeneralIcon />
        ) : (
          <FrameIcon />
        ),
        content: `${matGroupTitle || SectionTypes.MAT}`,
        hideDesktop: true,
      });
    }

    if (isMobile && variant?.model_file) {
      rightButtons.push({
        onClick: () => modelViewer.current?.activateAr(),
        content: textARButton,
        imageComponent: <SmallIcon />,
        tooltipText: 'View on your wall',
      });
    }

    if (userImg && !isGallery) {
      leftButtons.unshift({
        onClick: () => modelViewer.current?.edit(0),
        content: 'Edit',
        imageComponent: <EditIconV2 />,
      });
    }

    return {
      leftButtons,
      rightButtons,
      mainButtons: buttons,
    };
  }, [userImg, variant?.model_file, isMobile, colorCollection]);

  if (!product) return null;

  const colorName = isGallery && printSize.length > 0 ? printSize : productColor;
  const podColorName = colorName.charAt(0).toUpperCase() + colorName.slice(1);
  const priceBreakdown = calculatePrices({
    variant: { node: variant },
    isGallery,
    itemsLength: items.length,
  });

  return (
    <>
      <PodDevBar />
      <Container
        className={className}
        isEditingMode={false}
        isGallery={isGallery}
        is3dModel
        showStyleButton={showStyleButton}
        increaseAspectRatio={!window.location.href.includes('option-b')}
      >
        <Wrapper className="tmplt-product__media tmplt-product__media--bone" isGallery={isGallery}>
          <ModelViewerWrapper isGallery={isGallery} backgroundImage={galleryBackgroundImage}>
            <pod-frame-builder
              sku={variant?.sku}
              ref={modelViewer}
              variant={podColorName}
              // ar-banner={`Add to cart - <span style="text-decoration: line-through; opacity: 0.7">${
              //   priceBreakdown.comparePrice ?? ''
              // }</span> ${priceBreakdown.discountPrice}`}
              position={modelPosition}
              hidden
            >
              <span slot="empty-state">
                <CenteredUploadButton
                  onClick={() => modelViewer.current?.edit(0)}
                  title="Upload Photo"
                  icon={<UploadIcon />}
                />
              </span>
            </pod-frame-builder>
          </ModelViewerWrapper>

          {showProductInfo && !isGallery ? (
            <ProductContainer>
              <ProductHeaderV2 />
              <ProductInfoV2 />
              {/* <ProductReviewMockStyled /> */}
            </ProductContainer>
          ) : null}

          {isMobile && isGallery ? null : (
            <BottomButtons className="frame-builder-bottom-container" buttons={bottomButtons} />
          )}
        </Wrapper>
      </Container>
    </>
  );
};

export default FrameBuilder;
